import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ShieldCheck, TriangleAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const token = searchParams.get("token") || "";
  const [state, setState] = useState({ status: "verifying", message: "", already: false });

  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!email || !token) {
        setState({ status: "error", message: "This unsubscribe link is missing required parameters." });
        return;
      }
      try {
        const { data } = await api.get("/unsubscribe/verify", { params: { email, token } });
        if (!alive) return;
        setState({ status: "verified", message: "", already: !!data.already });
      } catch (err) {
        if (!alive) return;
        setState({ status: "error", message: formatApiError(err) });
      }
    };
    run();
    return () => { alive = false; };
  }, [email, token]);

  const confirm = async () => {
    setState((s) => ({ ...s, status: "submitting" }));
    try {
      await api.post("/unsubscribe", { email, token });
      setState({ status: "ok", message: "", already: true });
    } catch (err) {
      setState({ status: "error", message: formatApiError(err) });
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-brand-50 to-white flex items-center justify-center px-4 py-16"
      data-testid="page-unsubscribe"
    >
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl shadow-soft p-8 sm:p-10 text-center">
        {state.status === "verifying" && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-accent-100 flex items-center justify-center mb-5">
              <Loader2 className="h-7 w-7 text-accent-700 animate-spin" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-ink mb-2" data-testid="unsub-title-verifying">
              Verifying your unsubscribe link…
            </h1>
          </>
        )}

        {state.status === "verified" && !state.already && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-brand-50 flex items-center justify-center mb-5 border border-brand-100">
              <ShieldCheck className="h-7 w-7 text-brand-700" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-ink mb-2" data-testid="unsub-title-confirm">
              Unsubscribe from marketing emails
            </h1>
            <p className="text-sm text-slate-600 mb-6" data-testid="unsub-email">
              <span className="font-semibold">{email}</span>
            </p>
            <p className="text-sm text-slate-600 mb-8 leading-relaxed">
              You'll still receive <span className="font-semibold text-ink">transactional emails</span> — welcome, service-completion receipts, photo proof, and payment notices — because those are required to run your service. Only newsletters and marketing campaigns will be stopped.
            </p>
            <button
              type="button"
              onClick={confirm}
              className="btn-primary w-full"
              data-testid="unsub-confirm"
            >
              Unsubscribe
            </button>
            <Link
              to="/"
              className="block mt-3 text-xs text-slate-500 hover:text-brand-700"
              data-testid="unsub-cancel"
            >
              Never mind, go back
            </Link>
          </>
        )}

        {(state.status === "ok" || (state.status === "verified" && state.already)) && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
              <CheckCircle2 className="h-7 w-7 text-emerald-600" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-ink mb-2" data-testid="unsub-title-ok">
              You're unsubscribed
            </h1>
            <p className="text-sm text-slate-600 mb-6">
              <span className="font-semibold">{email}</span> has been removed from marketing emails. Transactional service emails will still send because they're required for your subscription.
            </p>
            <Link to="/" className="btn-primary w-full" data-testid="unsub-home">
              Back to home
            </Link>
          </>
        )}

        {state.status === "submitting" && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-accent-100 flex items-center justify-center mb-5">
              <Loader2 className="h-7 w-7 text-accent-700 animate-spin" />
            </div>
            <p className="text-sm text-slate-600">Saving your preference…</p>
          </>
        )}

        {state.status === "error" && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-red-100 flex items-center justify-center mb-5">
              <TriangleAlert className="h-7 w-7 text-red-600" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-ink mb-2" data-testid="unsub-title-error">
              We couldn't process that link
            </h1>
            <p className="text-sm text-slate-600 mb-6" data-testid="unsub-error-message">
              {state.message || "This unsubscribe link is invalid or expired."}
            </p>
            <p className="text-xs text-slate-500 mb-6">
              Reply to any email from us with &ldquo;STOP&rdquo; and we'll remove you manually.
            </p>
            <Link to="/contact" className="btn-secondary w-full" data-testid="unsub-contact">
              Contact support
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
